import { observer } from 'mobx-react';
import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ExportButton } from '~components/action-buttons';
import { PageTopBar } from '~components/page-top-bar';
import {
    useAnalyticsStore,
    useApplicationSelectionStore,
} from '~contexts/mobx';
import { AskAIButton } from '~analytics-components/ask-ai-button';
import { SectionHeader } from '~analytics-components/section-header';
import { KpiCardFromSpec } from '~containers/analytics/kpis/kpi-card-from-spec';
import { OverviewToolbar } from '~containers/analytics/overview-page/overview-toolbar';
import { usePageFilter } from '~containers/analytics/overview-page/use-page-filter';
import type { PageFilterQuery } from '~containers/analytics/overview-page/use-page-filter';
import type { PageCampaign } from '~containers/analytics/overview-page/page-filter';
import { lastSelectableDay } from '~containers/analytics/kpis/periods';
import { AnalyticsSearchOptions } from '~components/analytics-search-options';
import { useSearchParams } from '~utils/use-search-params';
import { KpiLoadProgressBar } from '~analytics-components/kpi-load-progress-bar';
import type { KpiCardSpec } from '~containers/analytics/kpis/card-spec';
import {
    OVERALL_PERFORMANCE_CARDS,
    BUSINESS_IMPACT_CARDS,
    GAME_ENGAGEMENT_CARDS,
    GAME_ENGAGEMENT_CHART_CARDS,
    COUPON_CARDS,
    COUPON_BREAKDOWN_CARDS,
    TECHNICAL_HEALTH_CARDS,
    ENGAGEMENT_DRIVER_CARDS,
} from './cards';
import style from './style.module.scss';

/**
 * The RPP edition the API resolves `period=campaign` for. The dates are only
 * the CMS's reading of it, shown before the answer.
 */
const RPP_CAMPAIGN: PageCampaign = {
    window: { startDate: '2026-07-21', endDate: '2026-09-14' },
    campaignId: 'rpp-2026',
};

/**
 * RPP dashboard. Same mechanism as the CT/CK analytics Overview: a filter toolbar
 * (date + region) over N sections, each a SectionHeader + a grid of KpiCardFromSpec.
 * Cards fetch through the standardized envelope sources; a filter change re-runs
 * each card's fetch. Request pacing + the load bar are handled in the store, so
 * this page just lays out cards and drops in a single KpiLoadProgressBar.
 */
export const RppDashboardPage = observer(() => {
    const applicationSelectionStore = useApplicationSelectionStore();
    const analyticsStore = useAnalyticsStore();
    const search = useSearchParams<PageFilterQuery>();
    const {
        filter,
        setPeriod,
        setPreset,
        setRegion,
        setCompare,
    } = usePageFilter({
        search,
        defaultPeriod: 'campaign',
        campaign: RPP_CAMPAIGN,
        region: {
            appTarget: applicationSelectionStore.appTarget,
            defaultProvincesCsv:
                applicationSelectionStore.getProvincesFromSelectedApplication,
        },
    });

    const renderCard = (spec: KpiCardSpec) => (
        <KpiCardFromSpec
            key={spec.title}
            spec={spec}
            pageRange={filter.range}
            pageRegion={filter.provinces}
            descriptionOnlyInfo
            compareEnabled={filter.compare}
            waveKey={filter.waveKey}
        />
    );

    return (
        <>
            <PageTopBar title="RPP Dashboard">
                <ExportButton
                    onClick={() =>
                        analyticsStore.downloadWeeklyReport(
                            'rpp-2026',
                            'RPP Weekly Campaign Report'
                        )
                    }
                    loading={analyticsStore.isDownloadingReport('rpp-2026')}
                >
                    Download weekly report
                </ExportButton>
                <AskAIButton />
            </PageTopBar>
            <div className={`${style.dashboard} content_with_margins`}>
                <OverviewToolbar
                    filters={
                        <AnalyticsSearchOptions
                            startDate={filter.range.start}
                            endDate={filter.range.end}
                            updateParams={setPeriod}
                            lastSelectableDay={lastSelectableDay()}
                            periodPresets={{
                                active: filter.preset,
                                onSelect: setPreset,
                                campaign: RPP_CAMPAIGN.window,
                            }}
                            compareEnabled={filter.compare}
                            onCompareChange={setCompare}
                            regionFilter={{
                                value: filter.provinces,
                                disabled: filter.regionLocked,
                                onChange: setRegion,
                            }}
                        />
                    }
                />

                <KpiLoadProgressBar />

                <section className={style.section}>
                    <SectionHeader
                        title="Overall performance"
                        subtitle="RPP vs full app"
                    />
                    <div className={`${style.grid} ${style.grid_3}`}>
                        {OVERALL_PERFORMANCE_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader
                        title="Business impact"
                        subtitle="RPP vs full app"
                    />
                    <span
                        className={`${style.section_note} footnote_description`}
                    >
                        <InfoCircleOutlined />
                        Business impact metrics are showing all Canada.
                    </span>
                    <div className={`${style.grid} ${style.grid_3}`}>
                        {BUSINESS_IMPACT_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader
                        title="Game engagement"
                        subtitle="plays, completion, drop-off"
                    />
                    <div className={`${style.grid} ${style.grid_4}`}>
                        {GAME_ENGAGEMENT_CARDS.map(renderCard)}
                    </div>
                    {/* Two columns, not three: the drop-off funnel is the only
                        chart left here and is `md` (span 2), so it fills the
                        row instead of leaving a third column empty. */}
                    <div className={`${style.grid} ${style.grid_2}`}>
                        {GAME_ENGAGEMENT_CHART_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader
                        title="Coupons"
                        subtitle="issued and redeemed"
                    />
                    <div className={`${style.grid} ${style.grid_2}`}>
                        {COUPON_CARDS.map(renderCard)}
                    </div>
                    {/* Same order as the row above, so each breakdown sits
                        under its own headline count. */}
                    <div className={`${style.grid} ${style.grid_2}`}>
                        {COUPON_BREAKDOWN_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader
                        title="Technical health"
                        subtitle="errors, sessions"
                    />
                    <span
                        className={`${style.section_note} footnote_description`}
                    >
                        <InfoCircleOutlined />
                        Error rate and failed sessions are showing all Canada.
                    </span>
                    <div className={`${style.grid} ${style.grid_3}`}>
                        {TECHNICAL_HEALTH_CARDS.map(renderCard)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader
                        title="Engagement drivers"
                        subtitle="badges and bonus actions"
                    />
                    <div className={`${style.grid} ${style.grid_2}`}>
                        {ENGAGEMENT_DRIVER_CARDS.map(renderCard)}
                    </div>
                </section>
            </div>
        </>
    );
});
